/**
 * "Vincular todos à CCB" em "Arquivos sem CCB" (E209.F3.T03, GUS-296 item 12b).
 *
 * O ZIP solto antes do número da CCB deixava cada arquivo para marcar e confirmar um por um (11
 * vezes na 29706925). Aqui, uma ação por CCB gravada vincula de uma vez todos os arquivos sem CCB:
 * grava um vínculo por arquivo, em ordem, e relê. A ação por arquivo continua no painel de cada um.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { UnidadeDoEnvioData } from '@/lib/abertura_do_caso';
import { vincularDocumento } from '@/lib/ccbs_da_abertura';

import type { BlocoDaCcb } from './blocosDasCcbs';

const t = ptBR.abertura.contratosDigitais;

export function VincularTodos(props: {
  client: AxiosInstance;
  caseId: string;
  soltos: readonly UnidadeDoEnvioData[];
  blocos: readonly BlocoDaCcb[];
  onRecarregar: () => Promise<void>;
}): JSX.Element | null {
  const { client, caseId, soltos, onRecarregar } = props;
  const [gravando, setGravando] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);
  const gravadas = props.blocos.filter((b) => b.ccb !== null);
  if (gravadas.length === 0 || soltos.length < 2) return null;

  async function vincularTodos(ccb: string): Promise<void> {
    setGravando(ccb);
    setFalhou(false);
    try {
      for (const u of soltos) await vincularDocumento(client, caseId, ccb, u.documento_id);
    } catch {
      setFalhou(true);
    } finally {
      // O que já se vinculou sai da lista mesmo com a falha de um arquivo no meio.
      try {
        await onRecarregar();
      } finally {
        setGravando(null);
      }
    }
  }

  return (
    <div className="mt-2 flex flex-col gap-2" data-testid="ccbs-vincular-todos">
      <div className="flex flex-wrap gap-3">
        {gravadas.map((b) => (
          <Button
            key={b.chave}
            type="button"
            size="sm"
            onClick={() => void vincularTodos(b.chave)}
            disabled={gravando !== null}
            loading={gravando === b.chave}
            data-testid={`ccbs-vincular-todos-${b.chave}`}
          >
            {t.vincularTodos
              .replace('{n}', String(soltos.length))
              .replace('{ccb}', t.rotulo.replace('{numero}', b.ccb?.numero ?? ''))}
          </Button>
        ))}
      </div>
      {falhou ? (
        <p role="alert" className="text-[13px] text-danger">
          {t.erroAoVincular}
        </p>
      ) : null}
    </div>
  );
}
